import {
    Button,
    Dialog,
    DialogContent,
    Grid,
    IconButton,
    Typography,
    Checkbox,
    Tooltip,
} from "@mui/material";
import React, { useState } from "react";
import {HighlightOff} from "@mui/icons-material";
import { Alert } from '@mui/material';
import {isEqual} from 'lodash';

type ProductShape = {
    Attributes?: Record<string, unknown>;
    [key: string]: unknown;
};

type ProductChangesShape = ProductShape & {
    Attributes?: Record<string, unknown>;
};

type VariesByOption = {
    value: string;
    label?: string;
    [key: string]: unknown;
};

type VariesByShape = {
    options?: VariesByOption[];
    fields?: VariesByOption[];
};

interface ProductSaveConfirmProps {
    saveOnlyProduct: () => void;
    saveProductWithVariants: () => void;
    open: boolean;
    setOpen: (open: boolean) => void;
    shouldUpdateVariations: boolean;
    initProduct: ProductShape;
    product: ProductShape;
    initVariesBy: VariesByShape;
    variesBy: VariesByShape;
    title?: string;
}

function ProductSaveConfirm(props: ProductSaveConfirmProps) {
    const {saveOnlyProduct, saveProductWithVariants, open, setOpen, shouldUpdateVariations} = props;
    const {initProduct, product, initVariesBy, variesBy} = props;
    const [applyToAllVariants, setApplyToAllVariants] = useState(false);

    // Find differences between two objects and return only changed attributes
    const getChangedAttributes = (oldObj: ProductShape, newObj: ProductShape): ProductShape => {
        if (!oldObj || !newObj) return {};
        
        const changes: ProductChangesShape = {};
        
        // Compare top-level properties
        Object.keys(newObj).forEach(key => {
            // Skip if property doesn't exist in old object
            if (!(key in oldObj)) {
                if (newObj[key] !== undefined && newObj[key] !== null) {
                    changes[key] = newObj[key];
                }
                return;
            }
            
            // Check if values are different
            if (!isEqual(oldObj[key], newObj[key])) {
                // Handle nested Attributes object specially
                if (key === 'Attributes' && typeof oldObj[key] === 'object' && typeof newObj[key] === 'object') {
                    const oldAttributes = oldObj.Attributes || {};
                    const newAttributes = newObj.Attributes || {};
                    changes.Attributes = {};

                    // Compare attributes
                    Object.keys(newAttributes).forEach(attrKey => {
                        if (!isEqual(oldAttributes[attrKey], newAttributes[attrKey])) {
                            if (changes.Attributes) {
                                changes.Attributes[attrKey] = newAttributes[attrKey];
                            }
                        }
                    });
                    
                    // If no attribute changes, remove empty Attributes object
                    if (Object.keys(changes.Attributes).length === 0) {
                        delete changes.Attributes;
                    }
                } else {
                    changes[key] = newObj[key];
                }
            }
        });
        
        return changes;
    };

    // Compare product level changes
    const getProductChanges = () => {
        return getChangedAttributes(initProduct, product);
    };

    // Compare variesBy objects and return changes
    const getVariesByChanges = (oldVariesBy: VariesByShape, newVariesBy: VariesByShape) => {
        const changes = { options: [], fields: [] };
        const compareArray = (oldArr = [], newArr = []) => {
            const oldMap = (oldArr || []).reduce((acc, item) => { acc[item.value] = item; return acc; }, {});
            const newMap = (newArr || []).reduce((acc, item) => { acc[item.value] = item; return acc; }, {});
            const added = Object.keys(newMap).filter(key => !(key in oldMap)).map(key => ({ status: 'added', ...newMap[key] }));
            const removed = Object.keys(oldMap).filter(key => !(key in newMap)).map(key => ({ status: 'removed', ...oldMap[key] }));
            const changed = Object.keys(newMap).filter(key => key in oldMap && JSON.stringify(newMap[key]) !== JSON.stringify(oldMap[key]))
                .map(key => ({ status: 'changed', from: oldMap[key], to: newMap[key] }));
            return [...added, ...removed, ...changed];
        };
        changes.options = compareArray(oldVariesBy?.options, newVariesBy?.options);
        changes.fields = compareArray(oldVariesBy?.fields, newVariesBy?.fields);
        return changes;
    };

    const formatKeyName = (key) => {
        return key
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    const formatValue = (value, maxLength = 50) => {
        if (value === null || value === undefined) return 'None';
        if (Array.isArray(value)) return value.join(', ');
        if (typeof value === 'object') return JSON.stringify(value, null, 2);
        const strValue = String(value);
        if (strValue.length > maxLength) {
            return strValue.slice(0, maxLength) + '...';
        }
        return strValue;
    };

    const getChangedAttributeEntries = (changes) => {
        if (!changes.Attributes) return [];
        return Object.entries(changes.Attributes).map(([key, value]) => ({
            key,
            value
        }));
    };

    const productChanges = getProductChanges();
    const variesByChanges = getVariesByChanges(initVariesBy, variesBy);
    const hasVariesByChanges =
        (variesByChanges.options && variesByChanges.options.length > 0) ||
        (variesByChanges.fields && variesByChanges.fields.length > 0);

    return (
        <Dialog open={open} maxWidth="md" fullWidth>
            <div style={{
    height: "10px",
    width: "100%",
    background: "#22b36e",
    transition: "background 0.5s ease"
}}/>
            <DialogContent style={{padding: "0"}}>
                <div style={{
                    padding: "20px",
                    position: "relative"
                }}>
                    <IconButton
                        style={{
                            position: "absolute",
                            right: "10px",
                            top: "10px"
                        }}
                        onClick={() => setOpen(false)}
                        size="large">
                        <HighlightOff style={{color: "red"}}/>
                    </IconButton>

                    <Typography variant="h5" style={{marginBottom: "20px", fontWeight: 600}}>
                        {props.title || "Confirm Attribute Updates"}
                    </Typography>

                    <Typography style={{marginBottom: "20px"}}>
                        Please confirm you would like to update the following attributes.
                    </Typography>

                    <Grid container spacing={4}>
                        <Grid item size={shouldUpdateVariations ? 6 : 12}>
                            <div style={{
                                backgroundColor: "#f5f5f5",
                                padding: "20px",
                                borderRadius: "4px"
                            }}>
                                <Typography variant="h6" style={{marginBottom: "15px"}}>
                                    {shouldUpdateVariations ? "Product Level Attribute Updates" : "Variant Level Attribute Updates"}
                                </Typography>
                                {/* VariesBy changes section */}
                                {hasVariesByChanges && (
                                  <div style={{marginBottom: "15px"}}>
                                    <Typography style={{fontWeight: 500, marginBottom: 4}}>
                                      <span>Variation Structure Changes</span>
                                    </Typography>
                                    {['options', 'fields'].map(type => (
                                      variesByChanges[type].length > 0 && (
                                        <div key={type} style={{marginBottom: 8}}>
                                          <Typography style={{fontWeight: 500}}>{type.charAt(0).toUpperCase() + type.slice(1)}:</Typography>
                                          {variesByChanges[type].map((change, idx) => {
                                            if (change.status === 'added') {
                                              return <Typography key={idx} style={{color: '#22b36e'}}>+ Added: <strong>{change.label || change.value}</strong></Typography>;
                                            } else if (change.status === 'removed') {
                                              return <Typography key={idx} style={{color: '#d32f2f'}}>- Removed: <strong>{change.label || change.value}</strong></Typography>;
                                            } else if (change.status === 'changed') {
                                              return <Typography key={idx} style={{color: '#fbc02d'}}>~ Changed: <strong>{change.from.label || change.from.value}</strong> → <strong>{change.to.label || change.to.value}</strong></Typography>;
                                            }
                                            return null;
                                          })}
                                        </div>
                                      )
                                    ))}
                                  </div>
                                )}
                                {(() => {
                                  const variantKeys = [
                                    ...(variesBy.options || []),
                                    ...(variesBy.fields || [])
                                  ].map(opt => opt.value);
                                  const filteredEntries = getChangedAttributeEntries(productChanges).filter(({key}) => !variantKeys.includes(key));
                                  return filteredEntries.length > 0 ? (
                                    filteredEntries.map(({key, value}) => (
                                      <div key={key} style={{marginBottom: "15px"}}>
                                        <Typography>
                                          <strong>{formatKeyName(key)}:</strong>{' '}
                                          {(() => {
                                            const formatted = formatValue(value);
                                            const strValue = String(value);
                                            return strValue.length > 50 ? (
                                              <Tooltip title={strValue}>
                                                <span style={{color: "#22b36e", cursor: "pointer"}}>{formatted}</span>
                                              </Tooltip>
                                            ) : (
                                              <span style={{color: "#22b36e"}}>{formatted}</span>
                                            );
                                          })()}
                                        </Typography>
                                      </div>
                                    ))
                                  ) : (
                                    <Typography color="textSecondary">
                                      No product level changes
                                    </Typography>
                                  );
                                })()}
                            </div>
                        </Grid>

                        {shouldUpdateVariations && (
                          <Grid item size={6}>
                              <div style={{
                                  backgroundColor: "#f5f5f5",
                                  padding: "20px",
                                  borderRadius: "4px"
                              }}>
                                  <Typography variant="h6" style={{marginBottom: "15px"}}>
                                      Variant Level Attribute Updates
                                  </Typography>
                                  {(() => {
                                    // Gather all variant-level attribute keys from variesBy.options and variesBy.fields
                                    const variantKeys = [
                                      ...(variesBy.options || []),
                                      ...(variesBy.fields || [])
                                    ].map(opt => opt.value);
                                    // Only show changed attributes that are in variantKeys, from productChanges
                                    const filteredEntries = getChangedAttributeEntries(productChanges).filter(({key}) => variantKeys.includes(key));
                                    const hasVariantLevelChanges = filteredEntries.length > 0;
                                    
                                    return hasVariantLevelChanges ? (
                                      <>
                                        {filteredEntries.map(({key, value}) => (
                                          <div key={key} style={{marginBottom: "15px"}}>
                                            <Typography>
                                              <strong>{formatKeyName(key)}:</strong>{' '}
                                              {(() => {
                                                const formatted = formatValue(value);
                                                const strValue = String(value);
                                                return strValue.length > 50 ? (
                                                  <Tooltip title={strValue}>
                                                    <span style={{color: "#22b36e", cursor: "pointer"}}>{formatted}</span>
                                                  </Tooltip>
                                                ) : (
                                                  <span style={{color: "#22b36e"}}>{formatted}</span>
                                                );
                                              })()}
                                            </Typography>
                                          </div>
                                        ))}
                                        <div style={{marginTop: "20px"}}>
                                          <div style={{ display: 'flex', alignItems: 'center' }}>
                                              <Checkbox color="primary"
                                                  checked={applyToAllVariants}
                                                  onChange={(e) => setApplyToAllVariants(e.target.checked)}
                                              />
                                              <Typography>
                                                  Copy and apply variant-level changes to all variants for this product?
                                              </Typography>
                                          </div>
                                        </div>
                                      </>
                                    ) : (
                                      <Typography color="textSecondary">
                                        No variant level changes
                                      </Typography>
                                    );
                                  })()}
                              </div>
                          </Grid>
                        )}
                    </Grid>

                    {shouldUpdateVariations ? (
                    <Alert severity="warning" style={{margin: '20px 0'}}>
                        Please note that any updates to product level fields will automatically be applied to all variants under the same product. Variant level attribute updates will only be applied to this specific variant.
                        <br/><br/>
                        If you would like to change an attribute from product-level to variant-level or vise versa, you may do so via the Manage Variations button on the Product Details page.
                    </Alert>
                    ) : (
                        <Alert severity="warning" style={{margin: '20px 0'}}>
                            Please note that this product does not have a designated <b>varies_by</b> attribute.
                            As a result, any updates to product attributes will be treated as variant-specific and will not 
                            be applied to other variants of the same product.
                            <br />
                            <br />
                            To specify the attributes that the product varies by, you can use the <b>Manage Variations</b> button
                            on the <b>Product Details</b> page.
                            <br />
                            <br />
                            For example, if a shirt is available in multiple <b>sizes</b> and <b>colors</b>, you should
                            include both the <b>size</b> and <b>color</b> attributes in the <b>varies_by</b> list to ensure proper
                            updates across all variants.
                        </Alert>
                    )}

                    <Grid container justifyContent="flex-end" style={{gap: "10px"}}>
                       <Button
                            color="primary"
                            variant="text"
                            onClick={() => {
                                setOpen(false);
                            }}
                            style={{marginTop: "20px"}}
                        >
                            Cancel
                        </Button>

                        <Button
                            color="primary"
                            variant="text"
                            onClick={() => {
                                if (applyToAllVariants) {
                                    saveProductWithVariants();
                                } else {
                                    saveOnlyProduct();
                                }
                            }}
                            style={{marginTop: "20px"}}
                        >
                            Save
                        </Button>
                    </Grid>
                </div>
            </DialogContent>
        </Dialog>
    );
}

export default ProductSaveConfirm;
